import type { HttpMethod, HttpToolParam } from '@kanbu/schema/contracts';
import { useLingui, Trans } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  Switch,
  TextArea,
  toast,
} from '@utima/ui';
import { useEffect, useState } from 'react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { HttpToolBodyEditor } from './HttpToolBodyEditor';
import { HttpToolHeaderRows, type HeaderRow } from './HttpToolHeaderRows';
import { HttpToolParamRows } from './HttpToolParamRows';
import { useHttpToolMutation } from '../hooks/useHttpToolMutation';

type HttpToolRow = RouterOutputs['httpTools']['findAll'][number];

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const BODY_METHODS = new Set<HttpMethod>(['POST', 'PUT', 'PATCH']);

export interface HttpToolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  httpTool?: HttpToolRow;
}

interface FormState {
  name: string;
  description: string;
  url: string;
  method: HttpMethod;
  pathParams: HttpToolParam[];
  queryParams: HttpToolParam[];
  headerRows: HeaderRow[];
  removedHeaderKeys: string[];
  bodyExample: string;
  bodyInstructions: string;
  responseMapping: string;
  timeoutMs: number;
  availableToAllChats: boolean;
  chatIds: string[];
  isActive: boolean;
}

function initialState(httpTool?: HttpToolRow): FormState {
  const params = httpTool?.parameters ?? [];

  return {
    name: httpTool?.name ?? '',
    description: httpTool?.description ?? '',
    url: httpTool?.url ?? '',
    method: httpTool?.method ?? 'GET',
    pathParams: params.filter(p => p.location === 'path'),
    queryParams: params.filter(p => p.location === 'query'),
    headerRows: [],
    removedHeaderKeys: [],
    bodyExample: httpTool?.bodyExample ?? '',
    bodyInstructions: httpTool?.bodyInstructions ?? '',
    responseMapping: httpTool?.responseMapping ?? '',
    timeoutMs: httpTool?.timeoutMs ?? 10000,
    availableToAllChats: httpTool?.availableToAllChats ?? false,
    chatIds: httpTool?.chatIds ?? [],
    isActive: httpTool?.isActive ?? true,
  };
}

function headerRowsToRecord(
  rows: HeaderRow[],
): Record<string, string> | undefined {
  const entries = rows
    .filter(row => row.key.trim() && row.value !== '')
    .map(row => [row.key.trim(), row.value] as const);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function HttpToolDialog({
  open,
  onOpenChange,
  httpTool,
}: HttpToolDialogProps) {
  const { t } = useLingui();
  const { create, update } = useHttpToolMutation();
  const [form, setForm] = useState<FormState>(initialState(httpTool));

  const isEdit = Boolean(httpTool);

  useEffect(() => {
    if (open) {
      setForm(initialState(httpTool));
    }
  }, [open, httpTool]);

  const { data: chats } = trpc.chats.findAll.useQuery({
    pagination: { pageSize: 1000 },
    fields: ['id', 'name'],
  });

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleChat = (chatId: string) =>
    setForm(prev => ({
      ...prev,
      chatIds: prev.chatIds.includes(chatId)
        ? prev.chatIds.filter(id => id !== chatId)
        : [...prev.chatIds, chatId],
    }));

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error(t`Name and URL are required.`);

      return;
    }

    if (form.description.trim().length < 10) {
      toast.error(t`Description must be at least 10 characters.`);

      return;
    }

    const showBody = BODY_METHODS.has(form.method);

    const headers = headerRowsToRecord(form.headerRows);
    const headerPatch = {
      ...Object.fromEntries(form.removedHeaderKeys.map(key => [key, null])),
      ...headers,
    };

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      url: form.url.trim(),
      method: form.method,
      parameters: [...form.pathParams, ...form.queryParams],
      // Empty strings survive JSON transport and clear a previous value;
      // undefined would omit the field and preserve it on the server.
      bodyExample: showBody ? form.bodyExample.trim() : '',
      bodyInstructions: showBody ? form.bodyInstructions.trim() : '',
      responseMapping: form.responseMapping.trim(),
      timeoutMs: form.timeoutMs,
      availableToAllChats: form.availableToAllChats,
      chatIds: form.availableToAllChats ? [] : form.chatIds,
      isActive: form.isActive,
      ...(httpTool
        ? Object.keys(headerPatch).length > 0
          ? { headerPatch }
          : {}
        : headers
          ? { headers }
          : {}),
    };

    try {
      await (httpTool
        ? update.mutateAsync({ id: httpTool.id, ...payload })
        : create.mutateAsync(payload));

      toast.success(t`HTTP tool saved.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to save HTTP tool.`,
      );
    }
  };

  const chatItems = (chats?.items ?? []) as { id: string; name: string }[];
  const isPending = create.isPending || update.isPending;
  const showBody = BODY_METHODS.has(form.method);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-[720px] max-w-[95vw]'>
        <Dialog.Header>
          <Dialog.Title>
            {isEdit ? (
              <Trans>Edit HTTP Tool</Trans>
            ) : (
              <Trans>Add HTTP Tool</Trans>
            )}
          </Dialog.Title>
        </Dialog.Header>

        <div className='max-h-[72vh] space-y-5 overflow-y-auto py-2'>
          <div className='space-y-1.5'>
            <Label htmlFor='http-name'>
              <Trans>Name</Trans>
            </Label>
            <Input
              id='http-name'
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder={t`e.g. create_ticket`}
            />
            <p className='text-xs text-muted-fg'>
              <Trans>
                Lowercase letters, digits and underscores; must start with a
                letter.
              </Trans>
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='http-description'>
              <Trans>Description</Trans>
            </Label>
            <TextArea
              id='http-description'
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              rows={2}
              placeholder={t`What this tool does — the model uses this to decide when to call it.`}
            />
          </div>

          <div className='flex gap-2'>
            <div className='w-36 space-y-1.5'>
              <Label htmlFor='http-method'>
                <Trans>Method</Trans>
              </Label>
              <Select
                id='http-method'
                value={form.method}
                onValueChange={value => setField('method', value as HttpMethod)}
              >
                {METHODS.map(method => (
                  <SelectItem key={method} value={method}>
                    {method}
                  </SelectItem>
                ))}
              </Select>
            </div>
            <div className='flex-1 space-y-1.5'>
              <Label htmlFor='http-url'>
                <Trans>URL</Trans>
              </Label>
              <Input
                id='http-url'
                value={form.url}
                onChange={e => setField('url', e.target.value)}
                placeholder='https://api.example.com/tickets/{id}'
              />
            </div>
          </div>

          <section className='space-y-2'>
            <div>
              <h4 className='text-sm font-semibold'>
                <Trans>Headers</Trans>
              </h4>
              <p className='text-xs text-muted-fg'>
                <Trans>
                  Static headers sent with every request (encrypted at rest).
                </Trans>
              </p>
            </div>
            <HttpToolHeaderRows
              rows={form.headerRows}
              onChange={rows => setField('headerRows', rows)}
              existingKeys={httpTool?.headerKeys}
              removedKeys={form.removedHeaderKeys}
              onToggleRemoval={key =>
                setField(
                  'removedHeaderKeys',
                  form.removedHeaderKeys.includes(key)
                    ? form.removedHeaderKeys.filter(item => item !== key)
                    : [...form.removedHeaderKeys, key],
                )
              }
            />
          </section>

          <section className='space-y-2'>
            <div>
              <h4 className='text-sm font-semibold'>
                <Trans>Path parameters</Trans>
              </h4>
              <p className='text-xs text-muted-fg'>
                <Trans>
                  Wrap a path segment in curly braces in the URL (e.g. {'{id}'})
                  and configure it here.
                </Trans>
              </p>
            </div>
            <HttpToolParamRows
              location='path'
              params={form.pathParams}
              onChange={params => setField('pathParams', params)}
            />
          </section>

          <section className='space-y-2'>
            <div>
              <h4 className='text-sm font-semibold'>
                <Trans>Query parameters</Trans>
              </h4>
              <p className='text-xs text-muted-fg'>
                <Trans>
                  Appended to the request query string. LLM params are collected
                  by the model; static params are sent as-is.
                </Trans>
              </p>
            </div>
            <HttpToolParamRows
              location='query'
              params={form.queryParams}
              onChange={params => setField('queryParams', params)}
            />
          </section>

          {showBody && (
            <section className='space-y-2'>
              <div>
                <h4 className='text-sm font-semibold'>
                  <Trans>Request body</Trans>
                </h4>
              </div>
              <HttpToolBodyEditor
                example={form.bodyExample}
                instructions={form.bodyInstructions}
                onExampleChange={value => setField('bodyExample', value)}
                onInstructionsChange={value =>
                  setField('bodyInstructions', value)
                }
              />
            </section>
          )}

          <div className='flex gap-2'>
            <div className='flex-1 space-y-1.5'>
              <Label htmlFor='http-mapping'>
                <Trans>Response mapping (optional)</Trans>
              </Label>
              <Input
                id='http-mapping'
                value={form.responseMapping}
                onChange={e => setField('responseMapping', e.target.value)}
                placeholder='data.items'
              />
            </div>
            <div className='w-40 space-y-1.5'>
              <Label htmlFor='http-timeout'>
                <Trans>Timeout (ms)</Trans>
              </Label>
              <Input
                id='http-timeout'
                type='number'
                value={form.timeoutMs}
                onChange={e =>
                  setField('timeoutMs', Number(e.target.value) || 10000)
                }
              />
            </div>
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='http-all-chats'>
              <Trans>Available to all chats</Trans>
            </Label>
            <Switch
              id='http-all-chats'
              checked={form.availableToAllChats}
              onCheckedChange={checked =>
                setField('availableToAllChats', checked)
              }
            />
          </div>

          {!form.availableToAllChats && (
            <div className='space-y-1.5'>
              <Label>
                <Trans>Assigned chats</Trans>
              </Label>
              <div className='max-h-40 space-y-1 overflow-y-auto rounded-radius border border-border p-2'>
                {chatItems.length === 0 && (
                  <p className='text-sm text-muted-fg'>
                    <Trans>No chats available.</Trans>
                  </p>
                )}
                {chatItems.map(chat => (
                  <label
                    key={chat.id}
                    className='flex cursor-pointer items-center gap-2 text-sm'
                  >
                    <input
                      type='checkbox'
                      checked={form.chatIds.includes(chat.id)}
                      onChange={() => toggleChat(chat.id)}
                    />
                    {chat.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className='flex items-center justify-between'>
            <Label htmlFor='http-active'>
              <Trans>Active</Trans>
            </Label>
            <Switch
              id='http-active'
              checked={form.isActive}
              onCheckedChange={checked => setField('isActive', checked)}
            />
          </div>
        </div>

        <Dialog.Footer>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button loading={isPending} onClick={handleSubmit}>
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
